import * as array from 'd3-array'
import * as d3 from 'd3-selection'
import * as shape from 'd3-shape'
import * as scale from 'd3-scale'
import { interpolate } from 'd3-interpolate'
import thresholdByColor from './threshold-by-color.js'

function foregroundData (thresholdLookup, data, thresholds, value) {
  let color = data.value && data.value.color

  value = typeof value === 'undefined' ? (data.value && data.value.raw) : value

  // The foreground arc that shows the current value
  if (data.highlightArcThreshold && typeof value === 'number' && data.colors) {
    // When `highlightArcThreshold` is set to true, the foreground arc will be colored the full range of its threshold
    return thresholdLookup(data.colors, value, color, thresholds)
  } else if (color) {
    return {
      /* This object contains the data for building a normal foreground arc */
      start: array.min(thresholds),
      end: typeof value === 'number' ? value : array.max(thresholds),
      color
    }
  } else {
    return null
  }
}

/*
 * Determines the way we display arcs
 */
function arcData (thresholdLookup, data, thresholds) {
  let min = array.min(thresholds)
    , max = array.max(thresholds)

  if (min === max) {
    // There's a chance all thresholds could be equal if they are calculated. We want to make sure
    // we always draw an arc, even though the thresholds arc will not be drawn.
    max = min + 1
  }

  var result = [{
    // This is the data for the base background arc
    start: min,
    end: max
  }, {
    // This object is for the background shadow on the base arc
    start: min,
    end: max
  }]

  let foreground = foregroundData(thresholdLookup, data, thresholds)

  // Make sure there is valid foreground data to draw
  if (foreground && (foreground.start !== undefined && foreground.end !== undefined)) {
    result.push(foreground)
  }

  return result
}

export default function (id, defs, dim, startAngle) {
  var arcWidth = 22
    , thresholdRingSpace = 2.2 // The amount of space to leave for the threshold rings

  var draw = function (selection, thresholds, value, highlightArcThreshold, thresholdLookup = thresholdByColor) {
    selection.each(function (data) {
      let arcs = arcData(thresholdLookup, data, thresholds)
        , scaler = scale.scaleLinear()
                        .domain(array.extent(thresholds))
                        .range([startAngle, startAngle * -1])
                        .clamp(true)
        , arc = shape.arc().innerRadius(dim.width / 2 - arcWidth)
                           .outerRadius(dim.width / 2 - thresholdRingSpace)
                           .startAngle(function (d) {
                             return scaler(d.start) * Math.PI / 180
                           })
                           .endAngle(function (d) {
                             return scaler(d.end) * Math.PI / 180
                           })
        , color = arcs[arcs.length - 1].color

      // Add the def elements needed by the arcs
      var defArcs = defs.selectAll('g.defs-arcs').data([arcs[0]])
        , defEnter = defArcs.enter()
                            .append('g')
                              .attr('class', 'defs-arcs')

      // Add the background arc shadow
      var filter = defEnter.selectAll('.arc-shadow')
                           .data([value])
                           .enter()
                           .append('filter')
                             .attr('id', 'arc-shadow-' + id)

      filter.append('feOffset')
              .attr('dx', 0)
              .attr('dy', 1)
      filter.append('feGaussianBlur')
              .attr('result', 'offset-blur')
              .attr('stdDeviation', '2')
      filter.append('feComposite')
              .attr('operator', 'out')
              .attr('in', 'SourceAlpha')
              .attr('in2', 'offset-blur')
              .attr('result', 'inverse')
      filter.append('feFlood')
              .attr('class', 'arc-background-shadow')
              .attr('result', 'color')
      filter.append('feComposite')
              .attr('operator', 'in')
              .attr('in', 'color')
              .attr('in2', 'inverse')

      var isDescendingThresholds = thresholds.length > 1 && thresholds[1] < thresholds[0]
        , _defArcs = defEnter.merge(defArcs)
        , radialGradient = _defArcs.selectAll('radialGradient')
                                   .data(function () {
                                     if (arcs.length < 3 && !highlightArcThreshold) {
                                       // This gauge doesn't have a radial gradient
                                       return []
                                     }
                                     let r = (dim.width - dim.padding.left) / 2

                                     return [{ r, stops: ['45%', '95%'] }]
                                   })
                                   .attr('r', d => d.r)
        , linearGradient = _defArcs.selectAll('linearGradient')
                                   .data(function () {
                                     return !highlightArcThreshold && typeof value === 'number' ? [{
                                       y1: '0%',
                                       y2: '0%',
                                       x1: isDescendingThresholds ? '100%' : '0%',
                                       x2: isDescendingThresholds ? '0%' : '100%',
                                       stops: ['0%', '100%']
                                     }] : []
                                   })
                                   .attr('x1', function (d) { return d.x1 })
                                   .attr('x2', function (d) { return d.x2 })

      radialGradient.exit().remove()
      linearGradient.exit().remove()

      radialGradient.enter()
                    .append('radialGradient')
                      .attr('class', 'arc-gradient')
                      .attr('cx', 0)
                      .attr('cy', 0)
                      .attr('r', d => d.r)
                      .attr('gradientUnits', 'userSpaceOnUse') // So we position this gradient on the needle base
                      .selectAll('stop')
                        .data(function (d) { return d.stops })
                        .enter()
                        .append('stop')
                          .attr('offset', String)
                          .attr('stop-color', color)

      linearGradient.enter()
                    .append('linearGradient')
                      .attr('class', 'arc-gradient')
                      .attr('x1', function (d) { return d.x1 })
                      .attr('x2', function (d) { return d.x2 })
                      .attr('y1', function (d) { return d.y1 })
                      .attr('y2', function (d) { return d.y2 })
                      .selectAll('stop')
                        .data(function (d) { return d.stops })
                        .enter()
                        .append('stop')
                          .attr('offset', String)
                          .attr('stop-color', color)

      defEnter.append('clipPath')
                .attr('id', id + '-arc-clippath')
                .attr('class', 'arc-clippath')
                .append('path')
                  .attr('d', arc)

      _defArcs.selectAll('.arc-gradient')
              .attr('id', id + '-arc-gradient')
              .selectAll('stop')
              .attr('stop-color', color)

      var group = d3.select(this)
                    .selectAll('g.arcs').data([arcs])
        , path = group.enter()
                      .append('g')
                        .attr('class', 'arcs')
                        .merge(group)
                        .selectAll('path')
                        .data(function (d) {
                          return d
                        })
        , _path = path.enter()
                      .append('path')
                        .attr('d', arc)
                        .merge(path)

      path.exit().remove()

      // The arc background
      _path.filter(function (d, i) {
             return i === 0
           })
           .attr('class', 'arc-background')
           .attr('d', arc)

      // The shadow over the arc background
      _path.filter(function (d, i) {
             return i === 1
           })
           .attr('class', 'arc-background-shadow')
           .attr('d', arc)
           .attr('filter', 'url(#arc-shadow-' + id + ')')
           .attr('clip-path', 'url(#' + id + '-arc-clippath)')

      // The value arc that is on top of the background arc
      let foreground = _path.filter(function (d, i) {
                               return i === 2
                             })
                             .attr('fill', 'url(#' +  id + '-arc-gradient)')

      if (highlightArcThreshold) {
        foreground.attr('d', arc)
      } else {
        foreground.transition() // Then transition it to the new position
                  .duration(value ? 300 : 0)
                  .attrTween('d', function (d) {
                   // If we have the current angle, use it as the start, otherwise use the end angle. This is only
                   // for interpolation, though.
                   var startAngle = this._currentAngle !== undefined ? this._currentAngle : scaler(d.end)
                     , i = interpolate(scaler.invert(startAngle), d.end)

                   // Only store the current angle if there is a data value. Otherwise the the arc is just used to cover
                   // from start arc to end arc
                   if (value !== undefined) {
                     this._currentAngle = scaler(d.end)
                   }

                   return function (t) {
                     return arc({
                       start: d.start,
                       end: i(t)
                     })
                   }
                 })
      }

    })
  }

  return draw
}
